import React, { useState, useEffect } from 'react';
import { Save, FolderOpen, Trash2, Check } from 'lucide-react';
import { FilterPreset, LeadFilters } from '@/types/filters';
import { cn } from '@/lib/utils';

interface FilterPresetManagerProps {
  currentFilters: LeadFilters;
  onLoadPreset: (filters: LeadFilters) => void;
  userId: string; // To store presets per user
  className?: string;
}

export const FilterPresetManager: React.FC<FilterPresetManagerProps> = ({
  currentFilters,
  onLoadPreset,
  userId,
  className
}) => {
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [saveError, setSaveError] = useState('');
  const [savedConfirmation, setSavedConfirmation] = useState(false);

  const STORAGE_KEY = `lead-filter-presets-${userId}`;

  // Load presets from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setPresets(parsed);
      }
    } catch (error) {
      console.error('Failed to load filter presets:', error);
    }
  }, [STORAGE_KEY]);

  // Save presets to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
    } catch (error) {
      console.error('Failed to save filter presets:', error);
    }
  }, [presets, STORAGE_KEY]);

  const hasActiveFilters = () => {
    return (
      currentFilters.entryDateFrom ||
      currentFilters.entryDateTo ||
      currentFilters.closureDateFrom ||
      currentFilters.closureDateTo ||
      currentFilters.lastContactFrom ||
      currentFilters.lastContactTo ||
      (currentFilters.stages && currentFilters.stages.length > 0) ||
      (currentFilters.priorities && currentFilters.priorities.length > 0) ||
      (currentFilters.assignedTo && currentFilters.assignedTo.length > 0) ||
      currentFilters.dealValueMin !== undefined ||
      currentFilters.dealValueMax !== undefined ||
      currentFilters.riskLevel !== undefined
    );
  };

  const handleSavePreset = () => {
    if (!hasActiveFilters()) {
      setSaveError('No filters to save. Please apply some filters first.');
      return;
    }

    if (!presetName.trim()) {
      setSaveError('Please enter a name for this preset.');
      return;
    }

    // Check for duplicate names
    if (presets.some(p => p.name.toLowerCase() === presetName.trim().toLowerCase())) {
      setSaveError('A preset with this name already exists.');
      return;
    }

    const newPreset: FilterPreset = {
      id: `preset-${Date.now()}`,
      name: presetName.trim(),
      filters: { ...currentFilters },
      createdAt: new Date().toISOString()
    };

    setPresets([...presets, newPreset]);
    setPresetName('');
    setSaveError('');
    setShowSaveDialog(false);

    // Show confirmation
    setSavedConfirmation(true);
    setTimeout(() => setSavedConfirmation(false), 2000);
  };

  const handleLoadPreset = (preset: FilterPreset) => {
    onLoadPreset(preset.filters);
    setShowLoadDialog(false);
  };

  const handleDeletePreset = (presetId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this preset?')) {
      setPresets(presets.filter(p => p.id !== presetId));
    }
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Save Preset Button */}
      <button
        type="button"
        onClick={() => setShowSaveDialog(true)}
        disabled={!hasActiveFilters()}
        className={cn(
          'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors',
          hasActiveFilters()
            ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
            : 'bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed'
        )}
        title={hasActiveFilters() ? 'Save current filters as preset' : 'Apply some filters first'}
      >
        <Save className="w-4 h-4" />
        Save Preset
      </button>

      {/* Load Preset Button */}
      <button
        type="button"
        onClick={() => setShowLoadDialog(true)}
        disabled={presets.length === 0}
        className={cn(
          'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors',
          presets.length > 0
            ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
            : 'bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed'
        )}
        title={presets.length > 0 ? 'Load saved preset' : 'No saved presets'}
      >
        <FolderOpen className="w-4 h-4" />
        Load Preset
        {presets.length > 0 && (
          <span className="ml-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
            {presets.length}
          </span>
        )}
      </button>

      {/* Saved Confirmation */}
      {savedConfirmation && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 text-green-700 text-sm font-medium rounded-md">
          <Check className="w-4 h-4" />
          Preset saved!
        </div>
      )}

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Save Filter Preset
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preset Name
                </label>
                <input
                  type="text"
                  value={presetName}
                  onChange={(e) => {
                    setPresetName(e.target.value);
                    setSaveError('');
                  }}
                  placeholder="e.g., Hot Leads This Month"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSavePreset();
                    }
                  }}
                />
                {saveError && (
                  <p className="text-xs text-red-600 mt-1">{saveError}</p>
                )}
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowSaveDialog(false);
                    setPresetName('');
                    setSaveError('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePreset}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Save Preset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Load Dialog */}
      {showLoadDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Load Filter Preset
            </h3>

            {presets.length === 0 ? (
              <p className="text-sm text-gray-600 py-4">
                No saved presets yet. Save your current filters to create a preset.
              </p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {presets.map((preset) => (
                  <div
                    key={preset.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleLoadPreset(preset)}
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {preset.name}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {new Date(preset.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleDeletePreset(preset.id, e)}
                      className="ml-2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete preset"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={() => setShowLoadDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
